import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  finalize,
  from,
  switchMap,
  take,
  throwError,
  of
} from 'rxjs';
import { CoreService } from '@services/common/core.service';
import { HttpHeaderService } from '@services/common/httprequest-header.service';
import { Router } from '@angular/router';
import { Routing } from '@core/constants/routing.const';
import { AuthEndpoints } from '@core/constants/endpoints.const';
import { ApiResultStatusCode } from '@core/enums/api-status-code.enum';
import { APP_CONFIG_SERIVCE } from './common/appconfig.service';
import { MessageService } from './common/message.service';
import { MessageType } from '@core/enums/message-type.enum';
import { IAppConfig } from '@models/app-config/app.config.model';
import { AuthService } from './auth/auth.service';
import { LogoutComponent } from '@hms/auth/logout/logout.component';
import { MatDialog } from '@angular/material/dialog';
import { getDialogConfig } from '@core/helpers/dialog-config.helper';
import { Messages } from '@core/constants/messages.const';
import { CurrentUserService } from './common/currentuser.service';
import { ILoginResponseModel } from '@models/auth/auth.model';
import { inject } from '@angular/core';


let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const mainInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const coreService        = inject(CoreService);
  const router             = inject(Router);
  const httpHeaderService  = inject(HttpHeaderService);
  const messageService     = inject(MessageService);
  const authService        = inject(AuthService);
  const currentUserService = inject(CurrentUserService);
  const dialog             = inject(MatDialog);
  const appConfig          = inject<IAppConfig>(APP_CONFIG_SERIVCE);

  if (coreService.displayLoader()) {
    coreService.showLoader();
  }

  return from(httpHeaderService.get()).pipe(
    switchMap((headers) => {
      let mergedHeaders = headers.set('ngsw-bypass', 'true');
      for (const key of request.headers.keys()) {
        const values = request.headers.getAll(key);
        if (values) {
          mergedHeaders = mergedHeaders.delete(key);
          for (const val of values) {
            mergedHeaders = mergedHeaders.append(key, val);
          }
        }
      }

      const isUrlRelative = !request.url.startsWith('http://') && !request.url.startsWith('https://');
      const targetUrl = isUrlRelative ? `${appConfig.apiEndpoint}${request.url}` : request.url;

      const enrichedRequest = request.clone({
        headers: mergedHeaders,
        url: targetUrl,
      });

  return next(enrichedRequest).pipe(
    switchMap((event: HttpEvent<unknown>) => {
      if (!(event instanceof HttpResponse)) {
        return of(event);
      }
        const body       = event.body as Record<string, unknown> | null;
        const statusCode = body?.['statusCode'] as ApiResultStatusCode | undefined;
        const message = body?.['message'] as string | undefined;

          switch (statusCode) {
            case ApiResultStatusCode.Forbidden:
              router.navigate([Routing.Forbidden]);
              return of(event);

            case ApiResultStatusCode.Unauthorized:
              // If the request that failed is the logout endpoint itself, do not attempt refresh
              if (enrichedRequest.url.includes(AuthEndpoints.Logout)) {
                return of(event);
              }
              // Body-level 401 — attempt a silent token refresh.
              return attemptTokenRefresh(enrichedRequest, next, authService, currentUserService);

            case ApiResultStatusCode.RequiredReLogin:
              openForceLogoutDialog(dialog, authService);
              return of(null as unknown as HttpEvent<unknown>);

            case ApiResultStatusCode.Error:
              messageService.showMessage(message ?? Messages.InternetConnectionError, MessageType.Error);
              return of(null as unknown as HttpEvent<unknown>);

            default:
              return of(event);
          }
        }),

        catchError((error: { status?: ApiResultStatusCode; message?: string }) => {
          switch (error?.status) {
            case ApiResultStatusCode.Forbidden:
              router.navigate([Routing.Forbidden]);
              break;

            case ApiResultStatusCode.Unauthorized:
              // If the request that failed is the logout endpoint itself, do not attempt refresh
              if (enrichedRequest.url.includes(AuthEndpoints.Logout)) {
                return throwError(() => error);
              }
              // HTTP-level 401 — attempt a silent token refresh.
              return attemptTokenRefresh(enrichedRequest, next, authService, currentUserService);

            case ApiResultStatusCode.RequiredReLogin:
              authService.logout().subscribe(() =>
                messageService.showMessage(error?.message ?? '', MessageType.Error)
              );
              break;

            default:
              messageService.showMessage(Messages.InternetConnectionError, MessageType.Error);
          }

          return throwError(() => error);
        }),

        finalize(() => coreService.hideLoader())
      ); // end next(enrichedRequest).pipe()
    })   // end switchMap(headers =>)
  );     // end from(getAsync()).pipe()
};
/**
 * Handles 401 responses by attempting a silent token refresh.
 * Uses a BehaviorSubject gate so that if multiple requests fail simultaneously,
 * only ONE refresh call is made and all others queue behind it.
 *
 * IMPORTANT: We use `from(currentUserService.getAsync())` before checking for a
 * refresh token. On page refresh, `currentUserService.get()` returns null
 * synchronously because the async storage load hasn't finished yet — causing a
 * false "no refresh token" that incorrectly triggers logout. `getAsync()` waits
 * for the init promise to resolve, so we always read the real stored value.
 */
function attemptTokenRefresh(
  failedRequest: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  currentUserService: CurrentUserService,
): Observable<HttpEvent<any>> {
  // Wait for the user to be loaded from storage before deciding what to do.
  return from(currentUserService.getAsync()).pipe(
    switchMap((currentUser) => {
      const hasRefreshToken = !!currentUser?.refreshToken;

      if (!hasRefreshToken) {
        currentUserService.logout();
        return throwError(() => new Error('Session expired: no refresh token available.'));
      }

      if (isRefreshing) {
        // Another request is already refreshing — queue behind it.
        return refreshTokenSubject.pipe(
          filter((token): token is string => token !== null),
          take(1),
          switchMap((token: string) => next(addAuthHeader(failedRequest, token)))
        );
      }

      // This request owns the refresh call.
      isRefreshing = true;
      refreshTokenSubject.next(null); // signal: refresh in-progress

      return authService.refreshToken().pipe(
        switchMap((response: any) => {
          isRefreshing = false;
          const body = response as { data?: ILoginResponseModel; body?: { data: ILoginResponseModel } };
          const data: ILoginResponseModel | undefined = body?.data ?? body?.body?.data;

          if (!data?.jwtToken) {
            // Refresh token was rejected (expired or revoked)
            currentUserService.logout();
            return throwError(() => new Error('Refresh token rejected — logging out.'));
          }

          // Persist the new tokens silently
          currentUserService.updateTokens(
            data.jwtToken,
            data.refreshToken,
            new Date(data.expiration)
          );

          // Signal all waiting requests with the new access token
          refreshTokenSubject.next(data.jwtToken);

          // Retry the original failed request with the new token
          return next(addAuthHeader(failedRequest, data.jwtToken));
        }),
        catchError((err) => {
          isRefreshing = false;
          refreshTokenSubject.next(null);
          currentUserService.logout();
          return throwError(() => err);
        })
      );
    })
  );
}

function openForceLogoutDialog(dialog: MatDialog, authService: AuthService): void {
  const dialogRef = dialog.open(
    LogoutComponent,
    getDialogConfig({ width: '420px' })
  );

  dialogRef.afterClosed().subscribe((confirmed: boolean) => {
    if (confirmed) {
      authService.logout().subscribe();
      dialog.closeAll();
    }
  });
}

/**
 * Returns a clone of `request` with the Authorization header replaced.
 */
function addAuthHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> 
{
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      'ngsw-bypass': 'true',
    },
  });
}
